import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQuoteLeft, faChevronDown, faStar,
  faPaperPlane, faCheckCircle,
  faSpinner, faCopy, faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin as faLinkedinBrand } from '@fortawesome/free-brands-svg-icons';
import './Testimonial.css';

var API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

var fadeUp  = { hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] } } };
var stagger = { hidden: {}, show: { transition: { staggerChildren: 0.09 } } };

function getInitials(name) {
  var parts = (name || '').trim().split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ─── Single testimonial card ────────────────────────────────── */
var TestimonialCard = function(props) {
  var t = props.testimonial;
  var openState = useState(false); var open = openState[0]; var setOpen = openState[1];

  return (
    <motion.article
      className={'tcard' + (open ? ' tcard--open' : '')}
      variants={fadeUp} layout
      aria-label={'Testimonial from ' + t.name}
    >
      <button
        className="tcard__header"
        onClick={function() { setOpen(function(v) { return !v; }); }}
        aria-expanded={open}
        aria-label={open ? 'Collapse' : 'Read testimonial from ' + t.name}
      >
        <div className="tcard__avatar" aria-hidden="true">{getInitials(t.name)}</div>
        <div className="tcard__meta">
          <span className="tcard__name">{t.name}</span>
          <span className="tcard__role">
            {t.position}{t.organization ? ' · ' + t.organization : ''}
          </span>
        </div>
        {t.linkedinUrl && (
          <a href={t.linkedinUrl} target="_blank" rel="noopener noreferrer"
            className="tcard__linkedin" onClick={function(e) { e.stopPropagation(); }}
            aria-label="View LinkedIn profile">
            <FontAwesomeIcon icon={faLinkedinBrand} />
          </a>
        )}
        <FontAwesomeIcon
          icon={faChevronDown}
          className={'tcard__chevron' + (open ? ' tcard__chevron--open' : '')}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div className="tcard__body"
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
            <div className="tcard__body-inner">
              <FontAwesomeIcon icon={faQuoteLeft} className="tcard__quote-icon" aria-hidden="true" />
              <p className="tcard__text">{t.message}</p>
              <div className="tcard__stars" aria-label="5 star rating">
                {[1,2,3,4,5].map(function(s) {
                  return <FontAwesomeIcon key={s} icon={faStar} className="tcard__star" aria-hidden="true" />;
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
};

/* ─── Submission form ────────────────────────────────────────── */
var TestimonialForm = function(props) {
  var refCode  = props.refCode;
  var formRef  = useRef(null);

  var nameState    = useState(''); var name    = nameState[0]; var setName    = nameState[1];
  var posState     = useState(''); var position= posState[0];  var setPosition= posState[1];
  var orgState     = useState(''); var org     = orgState[0];  var setOrg     = orgState[1];
  var msgState     = useState(''); var message = msgState[0];  var setMessage = msgState[1];
  var liState      = useState(''); var linkedin= liState[0];   var setLinkedin= liState[1];
  var honeypotState= useState(''); var honeypot= honeypotState[0]; var setHoneypot = honeypotState[1];
  var statusState  = useState(''); var status  = statusState[0]; var setStatus  = statusState[1];
  var loadingState = useState(false); var loading= loadingState[0]; var setLoading= loadingState[1];
  var doneState    = useState(false); var done   = doneState[0]; var setDone   = doneState[1];

  var handleSubmit = function(e) {
    e.preventDefault();
    if (!name.trim() || !position.trim() || !message.trim()) {
      setStatus('error:Please fill in your name, role, and testimonial.');
      return;
    }
    if (message.trim().length < 20) {
      setStatus('error:Please write at least 20 characters.');
      return;
    }
    setLoading(true); setStatus('');
    fetch(API_BASE + '/testimonials', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name:         name.trim(),
        position:     position.trim(),
        organization: org.trim() || undefined,
        message:      message.trim(),
        linkedinUrl:  linkedin.trim() || undefined,
        refCode:      refCode || undefined,
        website:      honeypot, // honeypot field
      }),
    })
      .then(function(r) { return r.json(); })
      .then(function() { setDone(true); })
      .catch(function() { setStatus('error:Something went wrong. Please try again.'); })
      .finally(function() { setLoading(false); });
  };

  if (done) {
    return (
      <motion.div className="tform-success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
        <FontAwesomeIcon icon={faCheckCircle} className="tform-success__icon" />
        <h3 className="tform-success__title">Thank you!</h3>
        <p className="tform-success__sub">
          Your testimonial has been received and is awaiting approval. I truly appreciate you taking the time.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div className="tform" variants={fadeUp} ref={formRef} id="submit-testimonial">
      <div className="tform__header">
        <span className="home-label">Share Your Experience</span>
        <h2 className="tform__title">Leave a Testimonial</h2>
        <p className="tform__sub">
          {refCode
            ? 'Jacob invited you to share your experience working with him.'
            : 'Worked with me? I would love to hear what you thought.'}
        </p>
      </div>

      {status && (
        <div className={'tform__status tform__status--' + (status.startsWith('success') ? 'success' : 'error')}>
          {status.replace(/^(success|error):/, '')}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* Honeypot — hidden from humans, bots fill it in */}
        <input
          type="text" name="website" value={honeypot} tabIndex={-1}
          onChange={function(e) { setHoneypot(e.target.value); }}
          style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
          aria-hidden="true" autoComplete="off"
        />

        <div className="tform__row">
          <div className="tform__group">
            <label className="tform__label">Full Name *</label>
            <input className="tform__input" type="text" placeholder="Jane Smith"
              value={name} onChange={function(e) { setName(e.target.value); }} required />
          </div>
          <div className="tform__group">
            <label className="tform__label">Job Title / Role *</label>
            <input className="tform__input" type="text" placeholder="Senior Developer"
              value={position} onChange={function(e) { setPosition(e.target.value); }} required />
          </div>
        </div>

        <div className="tform__row">
          <div className="tform__group">
            <label className="tform__label">Organisation</label>
            <input className="tform__input" type="text" placeholder="Acme Corp (optional)"
              value={org} onChange={function(e) { setOrg(e.target.value); }} />
          </div>
          <div className="tform__group">
            <label className="tform__label">LinkedIn Profile URL</label>
            <input className="tform__input" type="url" placeholder="https://linkedin.com/in/... (optional)"
              value={linkedin} onChange={function(e) { setLinkedin(e.target.value); }} />
          </div>
        </div>

        <div className="tform__group">
          <label className="tform__label">Your Testimonial *</label>
          <textarea className="tform__textarea" rows={5}
            placeholder="Share your honest experience working with Jacob..."
            value={message} onChange={function(e) { setMessage(e.target.value); }} required />
          <span className="tform__char">{message.length} / 1000</span>
        </div>

        <button type="submit" className="home-btn home-btn--primary tform__submit" disabled={loading}>
          {loading
            ? <><FontAwesomeIcon icon={faSpinner} spin /> Submitting...</>
            : <><FontAwesomeIcon icon={faPaperPlane} /> Submit Testimonial</>}
        </button>
      </form>
    </motion.div>
  );
};

/* ─── Share link card ────────────────────────────────────────── */
// eslint-disable-next-line no-unused-vars
var ShareLinkCard = function() {
  var shareUrl  = window.location.origin + '/testimonials?ref=client';
  var copiedState = useState(false); var copied = copiedState[0]; var setCopied = copiedState[1];

  function handleCopy() {
    navigator.clipboard.writeText(shareUrl).then(function() {
      setCopied(true); setTimeout(function() { setCopied(false); }, 2200);
    });
  }

  return (
    <motion.div className="tshare-card" variants={fadeUp}>
      <p className="tshare-card__label">Share with clients</p>
      <div className="tshare-card__url">{shareUrl}</div>
      <button className="home-btn home-btn--outline tshare-card__btn" onClick={handleCopy}>
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        {copied ? ' Copied!' : ' Copy Link'}
      </button>
    </motion.div>
  );
};

/* ─── Main component ─────────────────────────────────────────── */
var Testimonials = function() {
  var location = useLocation();
  var params   = new URLSearchParams(location.search);
  var refCode  = params.get('ref') || '';
  var isInvited = Boolean(refCode);

  var testimonialsState = useState([]); var testimonials = testimonialsState[0]; var setTestimonials = testimonialsState[1];
  var loadingState      = useState(true); var loading = loadingState[0]; var setLoading = loadingState[1];

  useEffect(function() {
    fetch(API_BASE + '/testimonials')
      .then(function(r) { return r.json(); })
      .then(function(data) { setTestimonials(Array.isArray(data) ? data : []); })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // If arrived via invite link — scroll to form
  useEffect(function() {
    if (isInvited) {
      setTimeout(function() {
        var el = document.getElementById('submit-testimonial');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 600);
    }
  }, [isInvited]);

  return (
    <motion.div className="testimonials-root" initial="hidden" animate="show" variants={stagger}>

      {/* Hero */}
      <section className="testimonials-hero" aria-label="Testimonials header">
        <div className="testimonials-hero__glow" aria-hidden="true" />
        <Container>
          <motion.span className="home-label" variants={fadeUp}>Social Proof</motion.span>
          <motion.h1 className="testimonials-hero__title" variants={fadeUp}>
            What people <em>say.</em>
          </motion.h1>
          <motion.p className="testimonials-hero__sub" variants={fadeUp}>
            {isInvited
              ? 'Jacob invited you to share your experience. Your words mean everything — scroll down to leave a testimonial.'
              : 'Feedback from clients, students, and collaborators I\'ve had the privilege of working with.'}
          </motion.p>
          <motion.div className="testimonials-hero__stat" variants={fadeUp}>
            <span className="testimonials-hero__stat-value">{testimonials.length}+</span>
            <span className="testimonials-hero__stat-label">Verified Testimonials</span>
          </motion.div>
        </Container>
      </section>

      {/* Approved testimonials */}
      <section className="testimonials-section" aria-labelledby="testimonials-heading">
        <Container>
          <motion.div className="home-section__header" variants={fadeUp}>
            <span className="home-label">Reviews</span>
            <h2 id="testimonials-heading" className="home-section__title">Testimonials</h2>
            <p className="home-section__sub">Click any card to read the full testimonial.</p>
          </motion.div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--clr-text-muted)' }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '1.5rem' }} />
            </div>
          )}

          {!loading && testimonials.length === 0 && (
            <motion.div className="t-empty" variants={fadeUp}>
              <p>No testimonials yet — be the first to share your experience.</p>
            </motion.div>
          )}

          {!loading && testimonials.length > 0 && (
            <motion.div className="testimonials-list" variants={stagger}>
              {testimonials.map(function(t) {
                return <TestimonialCard key={t.id} testimonial={t} />;
              })}
            </motion.div>
          )}
        </Container>
      </section>

      {/* Submission form */}
      <section className="testimonials-form-section" aria-label="Submit testimonial">
        <Container>
          <TestimonialForm refCode={refCode} />
        </Container>
      </section>

    </motion.div>
  );
};

export default Testimonials;